import type { AtelierAction } from '../../shared';
import { isSuperAction, type DuelBeat } from '../sim';
import { weaponMotion,isSuperAction as isWeaponSuper } from '../../shared/weapon-motion';
import {abilityDuration,abilityTime} from './duelPlayback';

/** One presentation schedule also drives HP receipts and the controls' busy state. */
export function duelTiming(beat:DuelBeat) {
  if(beat.weapon) {
    const cfg=weaponMotion(beat.weapon,isWeaponSuper(beat.action)?beat.action:'strike');
    const delay=beat.action==='guard'||beat.action==='feint'?.22:0;
    const heroContact=beat.action==='prayer'?[420]:cfg.contacts.map(t=>isWeaponSuper(beat.action)?t*1000:Math.round((t+delay)*1000));
    const enemyCfg=weaponMotion(beat.enemyWeapon??'sword','strike');
    if(beat.action==='guard') {
      // Defence is an incoming blow met by a ward, not a free attack followed
      // by retaliation. Both receipts belong to that same visible collision.
      const enemyStart=180;
      const enemyContacts=enemyCfg.contacts.map(t=>enemyStart+Math.round(t*1000));
      return {duration:enemyStart+Math.round(enemyCfg.duration*1000)+220,heroRecovery:enemyStart+Math.round(enemyCfg.duration*1000),
        heroContact:[...enemyContacts],enemyStart,enemyContact:enemyContacts.at(-1)!,enemyContacts};
    }
    const heroRecovery=Math.round((beat.action==='prayer'?.7:(isWeaponSuper(beat.action)?abilityDuration(beat.action):cfg.duration)+delay)*1000);
    const enemyStart=heroRecovery+60;
    const enemyDelay=beat.intent==='guard'||beat.intent==='feint'?.22:.18;
    const enemyContacts=enemyCfg.contacts.map(t=>enemyStart+Math.round((t+enemyDelay)*1000));
    const enemyContact=enemyContacts.at(-1)!;
    return {duration:isSuperAction(beat.action)?heroRecovery+100:enemyStart+Math.round(enemyCfg.duration*1000)+180,heroRecovery,
      heroContact,enemyStart,enemyContact,enemyContacts};
  }
  if(beat.action==='guard')return {duration:1000,heroRecovery:700,heroContact:[435],enemyStart:180,enemyContact:435,enemyContacts:[435]};
  const double=beat.action==='signature';
  return {duration:double?1280:1120,heroRecovery:double?660:510,heroContact:double?[175,505]:[255],enemyStart:double?660:510,enemyContact:double?915:765,enemyContacts:[double?915:765]};
}
export function duelActorPose(beat:DuelBeat,elapsedMs:number,enemy:boolean,winner:0|1|null=null) {
  if(beat.weapon)return armedDuelPose(beat,elapsedMs,enemy,winner);
  if(beat.action==='guard')return unarmedGuardPose(beat,elapsedMs,enemy,winner);
  if(elapsedMs<0)return {action:'idle' as const,actionTime:0,advance:0,flash:0};
  const timing=duelTiming(beat),start=enemy?timing.enemyStart:0;
  let local=(elapsedMs-start)/1000,duration=.5;
  if(!enemy&&beat.action==='signature'){duration=.33;local=elapsedMs<330?elapsedMs/1000:(elapsedMs-330)/1000;}
  const ritual=enemy&&beat.rivalPhase==='chant'&&!beat.interrupted&&winner!==0;
  const chosen:AtelierAction=enemy?(ritual?'prayer':beat.intent):beat.action==='signature'?'strike':beat.action;
  let action:AtelierAction='idle',actionTime=0,advance=0,flash=0;
  if(chosen==='guard'&&(!enemy&&elapsedMs<timing.duration-100||enemy&&elapsedMs<timing.enemyStart))action='guard';
  if(local>=0&&local<duration&&(!enemy||winner!==0&&beat.action!=='signature'&&(!beat.noReply||ritual))) {
    action=chosen;actionTime=local/duration*(chosen==='strike'?2.4:.7);
    const damage=enemy?beat.received:beat.dealt;
    if(chosen==='feint'&&damage>0&&local>=.14&&local<.43){action='strike';actionTime=.65+(local-.14)/.29*.9;}
    if(chosen!=='prayer'&&chosen!=='guard')advance=Math.sin(local/duration*Math.PI)*.44;
  }
  const contacts=enemy?timing.heroContact:[timing.enemyContact],damage=enemy?beat.dealt:beat.received;
  const contact=contacts.find(at=>elapsedMs>=at&&elapsedMs<at+190);
  if(contact!==undefined&&damage>0){flash=.3;if(action==='idle'){action='hit';actionTime=(elapsedMs-contact)/1000;}}
  return {action,actionTime,advance,flash};
}

function unarmedGuardPose(beat:DuelBeat,ms:number,enemy:boolean,winner:0|1|null) {
  const t=duelTiming(beat),out:{action:AtelierAction;actionTime:number;advance:number;flash:number}={action:'idle',actionTime:0,advance:0,flash:0};
  if(ms<0||ms>=t.duration)return out;
  if(!enemy){out.action='guard';out.actionTime=guardClipTime(ms,t.enemyContacts);}
  else if(beat.rivalPhase==='chant'&&!beat.interrupted&&winner!==0){out.action='prayer';out.actionTime=ms/1000;}
  else if(!beat.noReply&&ms>=t.enemyStart&&ms<t.enemyStart+500&&(winner!==0||ms<=t.enemyContact)){
    const local=(ms-t.enemyStart)/1000;
    out.action=beat.intent==='guard'?'guard':'strike';out.actionTime=out.action==='guard'?guardClipTime(ms,t.enemyContacts):local/.5*2.4;
    if(out.action==='strike')out.advance=Math.sin(local/.5*Math.PI)*.44;
  }
  if(ms>=t.enemyContact&&ms<t.enemyContact+190&&(enemy?beat.dealt:beat.received)>0){out.flash=.3;
    if(enemy&&(winner===0&&ms>t.enemyContact||out.action==='idle')){out.action='hit';out.actionTime=(ms-t.enemyContact)/1000;}}
  return out;
}

function armedDuelPose(beat:DuelBeat,ms:number,enemy:boolean,winner:0|1|null) {
  const timing=duelTiming(beat),out:{action:AtelierAction;actionTime:number;advance:number;flash:number;lift:number}={action:'idle',actionTime:0,advance:0,flash:0,lift:0};
  if(ms<0||ms>=timing.duration)return out;
  const weapon=enemy?beat.enemyWeapon??'sword':beat.weapon!;
  const specialAction=!enemy&&isWeaponSuper(beat.action)?beat.action:null;
  const special=specialAction!==null;
  const ritual=enemy&&beat.rivalPhase==='chant'&&!beat.interrupted&&winner!==0;
  // A lower-cost technique can provoke damage from a guarding rival. Show
  // its actual guarded weapon counter after the clip, not HP loss from idle.
  // The player's own Guard and legacy defending-rival semantics stay intact.
  const blockedAnswer=(beat.techniqueBlocked??0)>0||(beat.shieldBlocked??0)>0;
  const guardedCounter=enemy&&isWeaponSuper(beat.action)&&!isSuperAction(beat.action)&&beat.intent==='guard'&&(beat.received>0||blockedAnswer);
  const chosen:AtelierAction=enemy?(ritual?'prayer':guardedCounter?'strike':beat.intent):special?beat.action as AtelierAction:beat.action==='signature'?'strike':beat.action;
  const cfg=weaponMotion(weapon,specialAction??'strike');
  const local=(ms-(enemy?timing.enemyStart:0))/1000;
  const delay=guardedCounter||chosen==='guard'||chosen==='feint'?.22:enemy?.18:0;
  const damage=enemy?beat.received:beat.dealt;
  if(beat.action==='guard') {
    if(!enemy){out.action='guard';out.actionTime=guardClipTime(ms,timing.enemyContacts);}
    else if(ritual){out.action='prayer';out.actionTime=Math.max(0,local);}
    else if(!beat.noReply&&local>=0&&local<cfg.duration&&(winner!==0||ms<=timing.enemyContact)) {
      out.action=beat.intent==='guard'?'guard':'strike';
      out.actionTime=out.action==='guard'?guardClipTime(ms,timing.enemyContacts):local;
      if(out.action==='strike')out.advance=approach(local,cfg.contacts,cfg.duration);
    }
    const contact=timing.enemyContacts.find(at=>ms>=at&&ms<at+170);
    if(contact!==undefined&&(enemy?beat.dealt:beat.received)>0){out.flash=.3;
      if(enemy&&(winner===0&&ms>timing.enemyContact||out.action==='idle')){out.action='hit';out.actionTime=(ms-contact)/1000;}}
    return out;
  }
  const canAnswer=!enemy||winner!==0&&!isSuperAction(beat.action)&&(!beat.noReply||ritual);
  if(chosen==='guard'){out.action='guard';out.actionTime=guardClipTime(ms,enemy?timing.heroContact:timing.enemyContacts);}
  if(canAnswer&&local>=0&&local<(specialAction?abilityDuration(specialAction):cfg.duration)+delay) {
    if(chosen==='prayer'){out.action='prayer';out.actionTime=local;}
    else if(local<delay){out.action=chosen==='strike'?'guard':chosen;out.actionTime=local;}
    else if(chosen!=='guard'){out.action=special?chosen:'strike';out.actionTime=specialAction?abilityTime(specialAction,local-delay):local-delay;
      // Approach begins before contact and eases back during the authored
      // recovery. Rapid slashes share one approach, avoiding whole-body jitter.
      out.advance=special?0:approach(out.actionTime,cfg.contacts,cfg.duration);
    }
  }
  if(!enemy&&blockedAnswer&&!beat.noReply&&winner!==0&&ms>=timing.enemyStart){out.action='guard';out.actionTime=guardClipTime(ms,timing.enemyContacts);out.advance=0;}
  const contacts=enemy?timing.heroContact:timing.enemyContacts;
  const contact=contacts.find(at=>ms>=at&&ms<at+170);
  if(contact!==undefined&&(enemy?beat.dealt:beat.received)>0){out.flash=.3;if(out.action==='idle'){out.action='hit';out.actionTime=(ms-contact)/1000;}}
  return out;
}

const smooth=(x:number)=>{const v=Math.max(0,Math.min(1,x));return v*v*(3-2*v);};
function approach(t:number,contacts:readonly number[],duration:number) {
  const first=contacts[0],last=contacts.at(-1)!;
  return .44*smooth(t/Math.max(.15,first-.04))*(1-smooth((t-last-.08)/Math.max(.1,duration-last-.08)));
}
/** Rig guard keys: gather 0, brace .18, impact .36, recoil .48, hold .72. */
export function guardClipTime(ms:number,contacts:readonly number[]):number {
  const first=contacts[0];
  if(ms<=first)return .36*smooth(Math.max(0,ms)/first);
  const next=contacts.find(at=>at>ms),previous=[...contacts].reverse().find(at=>at<=ms)!;
  if(next!==undefined){const k=(ms-previous)/(next-previous);return .36+.13*Math.sin(k*Math.PI);}
  return .36+.36*smooth((ms-previous)/360);
}

/** Simulated end-of-turn health is revealed only at its visible damage/heal contact. */
export function duelDisplayedHp(beat:DuelBeat,before:[number,number],max:[number,number],elapsedMs:number):[number,number] {
  const timing=duelTiming(beat),heroContacts=timing.heroContact.filter(at=>elapsedMs>=at).length;
  const heroDone=heroContacts===timing.heroContact.length,enemyDone=elapsedMs>=timing.enemyContact;
  const received=Math.round(beat.received*timing.enemyContacts.filter(at=>elapsedMs>=at).length/timing.enemyContacts.length);
  const hero=Math.min(max[0],before[0]+(heroDone?beat.healed:0))-received;
  const foe=before[1]-Math.round(beat.dealt*heroContacts/timing.heroContact.length)+(enemyDone?beat.foeHealed??0:0);
  return [Math.max(0,hero),Math.max(0,Math.min(max[1],foe))];
}

/** Paid costs and gains follow the existing turn UI; a levy lands with the
 * collector's visible reply, never while his sword is still being raised. */
export function duelDisplayedFervor(beat:DuelBeat|undefined,settled:number,elapsedMs:number):number {
  return settled+(beat&&elapsedMs<duelTiming(beat).enemyContact?(beat.fervorTaken??0):0);
}
